/**
 * Subdivision and Development Layers Module
 *
 * Handles loading and rendering GeoJSON point layers for future subdivisions
 * and upcoming projects on the map.
 */

import { SUBDIVISION_LAYERS } from './config.js';
import { createSubdivisionPopup } from './popups.js';
import mapboxgl from 'mapbox-gl';

/**
 * Add all subdivision layers to the map
 * @param {mapboxgl.Map} map - The map instance
 */
export async function addSubdivisionLayers(map) {
  for (const layer of SUBDIVISION_LAYERS) {
    try {
      // Fetch the GeoJSON data
      const response = await fetch(layer.dataFile);
      const geojson = await response.json();

      console.log(`Loaded ${layer.name}: ${geojson.features.length} features`);

      // Add source
      map.addSource(layer.id, {
        type: 'geojson',
        data: geojson
      });

      // Add fill layer (polygon)
      map.addLayer({
        id: `${layer.id}-fill`,
        type: 'fill',
        source: layer.id,
        layout: {
          visibility: layer.visible ? 'visible' : 'none'
        },
        paint: {
          'fill-color': layer.style.fillColor || layer.style.circleColor,
          'fill-opacity': layer.style.fillOpacity || 0.4
        }
      });

      // Add line layer (polygon border)
      map.addLayer({
        id: `${layer.id}-line`,
        type: 'line',
        source: layer.id,
        layout: {
          visibility: layer.visible ? 'visible' : 'none'
        },
        paint: {
          'line-color': layer.style.lineColor || layer.style.circleStrokeColor || '#ffffff',
          'line-width': layer.style.lineWidth || 2
        }
      });

      // Add click handler for popups
      map.on('click', `${layer.id}-fill`, (e) => {
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const coordinates = e.lngLat;

          // Create popup content
          const content = createSubdivisionPopup(feature.properties, layer.type);

          // Create and display popup
          new mapboxgl.Popup({
            maxWidth: '400px',
            className: 'subdivision-popup',
            closeButton: true,
            closeOnClick: false
          })
            .setLngLat(coordinates)
            .setHTML(content)
            .addTo(map);
        }
      });

      // Change cursor on hover
      map.on('mouseenter', `${layer.id}-fill`, () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', `${layer.id}-fill`, () => {
        map.getCanvas().style.cursor = '';
      });

      console.log(`Added layer: ${layer.name}`);
    } catch (error) {
      console.error(`Error loading ${layer.name}:`, error);
    }
  }
}

/**
 * Toggle subdivision layer visibility
 * @param {mapboxgl.Map} map - The map instance
 * @param {string} layerId - The layer ID to toggle
 * @param {boolean} visible - Whether the layer should be visible
 */
export function toggleSubdivisionLayerVisibility(map, layerId, visible) {
  const visibility = visible ? 'visible' : 'none';

  // Toggle both fill and line layers
  if (map.getLayer(`${layerId}-fill`)) {
    map.setLayoutProperty(`${layerId}-fill`, 'visibility', visibility);
  }
  if (map.getLayer(`${layerId}-line`)) {
    map.setLayoutProperty(`${layerId}-line`, 'visibility', visibility);
  }

  console.log(`Subdivision layer ${layerId} visibility: ${visibility}`);
}
